/**
 * CECL Disclosure — ASC 326-20-50-13 roll-forward (Phase 16 Track B).
 *
 * Standalone disclosure page showing beginning balance, provisions,
 * writeoffs, recoveries, ending balance, plus pool-evaluated vs
 * individually-evaluated split per ASC 326-20-50-11.
 *
 * Also houses the Backfill Assignments admin action for populating
 * CECL basis on legacy contracts.
 */
import { useState, useEffect, useCallback } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import { useGlobalEntity, withEntityParam } from '../hooks/useGlobalEntity';
import { useApp } from '../contexts/AppContext';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import TabBar from '../components/ui/TabBar';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import Modal from '../components/ui/Modal';

const REV_API = `${API_V1}/revenue`;

// ── Types ──────────────────────────────────────────────────────

interface RollForward {
  asc_paragraph: string;
  period_months: number;
  as_of_date: string;
  period_start: string;
  beginning_balance: number;
  provisions_current_period_sales: number;
  changes_to_preexisting: number;
  writeoffs: number;
  recoveries: number;
  ending_balance: number;
  individually_evaluated_total: number;
  pool_evaluated_total: number;
  notes: string | null;
}

interface BackfillResponse {
  total_contracts_scanned: number;
  auto_assigned_count: number;
  needs_review_count: number;
  unassignable_count: number;
  auto_assigned_contract_ids: number[];
  needs_review_contract_ids: number[];
  unassignable_contract_ids: number[];
}

// ── Period options ─────────────────────────────────────────────

type PeriodKind = '12' | '24' | '36' | 'custom';

// ── Component ──────────────────────────────────────────────────

export default function CECLDisclosure() {
  const { entity } = useGlobalEntity();
  const { authUser } = useApp();
  const navigate = useNavigate();
  const [period, setPeriod] = useState<PeriodKind>('12');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const [data, setData] = useState<RollForward | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Backfill modal state
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillResult, setBackfillResult] = useState<BackfillResponse | null>(null);
  const [backfillTab, setBackfillTab] = useState<string>('auto');

  const isAdmin = authUser?.role?.toUpperCase() === 'ADMIN' || !!authUser?.is_admin;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url: string;
      if (period === 'custom') {
        if (!customFrom || !customTo) {
          setLoading(false);
          return;
        }
        url = withEntityParam(
          `${REV_API}/cecl/roll-forward?period_start=${encodeURIComponent(customFrom)}&as_of_date=${encodeURIComponent(customTo)}`,
          entity,
        );
      } else {
        url = withEntityParam(`${REV_API}/cecl/roll-forward?period_months=${period}`, entity);
      }
      const r = await authFetch(url);
      if (r.ok) {
        setData(await r.json());
      } else {
        const err = await r.json().catch(() => ({}));
        setError(err.detail || `HTTP ${r.status}`);
      }
    } catch (e: any) {
      setError(e?.message || 'Network error');
    }
    setLoading(false);
  }, [entity, period, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  // ── Backfill ──────────────────────────────────────────────────

  const runBackfill = async () => {
    setBackfillRunning(true);
    try {
      const r = await authFetch(withEntityParam(`${REV_API}/cecl/backfill-assignments`, entity), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (r.ok) {
        const result: BackfillResponse = await r.json();
        setBackfillResult(result);
      } else {
        const err = await r.json().catch(() => ({}));
        setBackfillResult(null);
        setError(`Backfill failed: ${err.detail || `HTTP ${r.status}`}`);
      }
    } catch (e: any) {
      setError(`Backfill failed: ${e?.message || 'Network error'}`);
    }
    setBackfillRunning(false);
  };

  const openBackfill = () => {
    setBackfillOpen(true);
    setBackfillResult(null);
    setBackfillTab('auto');
  };

  // ── Contract-id table helpers ─────────────────────────────────

  const idCols: Column[] = [
    {
      key: 'id', label: 'Contract ID',
      render: (v: number) => (
        <span style={{ fontWeight: 600, color: 'var(--accent)', cursor: 'pointer' }}
          onClick={() => navigate(`/contracts/${v}`)}>
          #{v}
        </span>
      ),
    },
    {
      key: '_link', label: '', align: 'right',
      render: (_: any, row: { id: number }) => (
        <ActionButton
          label="Open"
          size="sm"
          variant="secondary"
          onClick={(e) => { e.stopPropagation(); navigate(`/contracts/${row.id}`); }}
        />
      ),
    },
  ];

  const tabRows = (ids: number[] | undefined) =>
    (ids || []).map(id => ({ id }));

  // ── Render ────────────────────────────────────────────────────

  return (
    <PageLayout
      title="CECL Disclosure"
      subtitle="ASC 326-20-50-13 roll-forward — allowance for credit losses on contract assets"
      actions={isAdmin ? [
        { label: 'Backfill Assignments', onClick: openBackfill, variant: 'secondary' },
      ] : undefined}
    >
      {/* Period selector */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Period:</label>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value as PeriodKind)}
          style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 13 }}
        >
          <option value="12">12 months (default)</option>
          <option value="24">24 months</option>
          <option value="36">36 months</option>
          <option value="custom">Custom range</option>
        </select>

        {period === 'custom' && (
          <>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>From:</label>
            <input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 13 }}
            />
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>To:</label>
            <input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 13 }}
            />
          </>
        )}

        <button
          onClick={load}
          style={{ padding: '6px 12px', borderRadius: 4, border: '1px solid var(--border-subtle)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer' }}
        >
          Refresh
        </button>

        {data && (
          <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-secondary)' }}>
            {data.period_start} → <strong>{data.as_of_date}</strong>
          </div>
        )}
      </div>

      {error && (
        <div style={{
          padding: '10px 14px', marginBottom: 12, borderRadius: 6,
          background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)',
          color: 'var(--accent-critical)', fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {loading && !data && (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</div>
      )}

      {data && (
        <>
          {/* Roll-forward KPIs */}
          <KpiGrid columns={6} items={[
            {
              label: 'Beginning Balance',
              value: formatAmountCompact(data.beginning_balance),
            },
            {
              label: 'Provisions (Current Sales)',
              value: formatAmountCompact(data.provisions_current_period_sales),
              color: 'var(--warning)',
            },
            {
              label: 'Changes to Pre-existing',
              value: formatAmountCompact(data.changes_to_preexisting),
              color: data.changes_to_preexisting >= 0 ? 'var(--warning)' : 'var(--positive)',
            },
            {
              label: 'Writeoffs',
              value: formatAmountCompact(data.writeoffs),
              color: 'var(--accent-critical)',
            },
            {
              label: 'Recoveries',
              value: formatAmountCompact(data.recoveries),
              color: 'var(--positive)',
            },
            {
              label: 'Ending Balance',
              value: formatAmountCompact(data.ending_balance),
              color: 'var(--accent)',
            },
          ]} />

          {/* Evaluation basis split */}
          <div style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 24,
          }}>
            <div style={{
              padding: 16, border: '1px solid var(--border-subtle)', borderRadius: 8,
              background: 'var(--bg-card)',
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                Pool-evaluated (ASC 326-20-50-11)
              </div>
              <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--accent)', fontVariantNumeric: 'tabular-nums' }}>
                {formatAmount(data.pool_evaluated_total)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 6 }}>
                Contracts assessed via pool-based methodology — LOSS_RATE or WARM with Q-factor overlay.
              </div>
            </div>
            <div style={{
              padding: 16, border: '1px solid var(--border-subtle)', borderRadius: 8,
              background: 'var(--bg-card)',
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                Individually-evaluated (ASC 326-20-50-11)
              </div>
              <div style={{ fontSize: 26, fontWeight: 700, color: 'var(--warning)', fontVariantNumeric: 'tabular-nums' }}>
                {formatAmount(data.individually_evaluated_total)}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-hint)', marginTop: 6 }}>
                Contracts whose risk characteristics diverge from the pool and carry a specific reserve per ASC 326-20-30-5.
              </div>
            </div>
          </div>

          {/* Footnote narrative */}
          <div style={{
            padding: 16, borderRadius: 8,
            background: 'rgba(59,130,246,0.06)',
            border: '1px solid rgba(59,130,246,0.20)',
            marginBottom: 16,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
              Footnote Narrative — {data.asc_paragraph}
            </div>
            <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>
              {data.notes || 'No additional narrative provided for this period.'}
            </div>
          </div>

          <div style={{ fontSize: 11, color: 'var(--text-hint)', lineHeight: 1.5 }}>
            This disclosure is generated live from the CECL event ledger. Reviewer sign-off is required at period close before external filing.
            Amounts are in functional currency for the selected entity scope.
          </div>
        </>
      )}

      {/* Backfill modal */}
      <Modal
        open={backfillOpen}
        onClose={() => { setBackfillOpen(false); setBackfillResult(null); }}
        size="lg"
        title="Backfill CECL Assignments"
        description="Scans existing contracts with contract-asset exposure and assigns each to the best-matching pool (or flags for review)."
        footer={
          <>
            <ActionButton
              label="Close"
              variant="secondary"
              onClick={() => { setBackfillOpen(false); setBackfillResult(null); }}
              disabled={backfillRunning}
            />
            {!backfillResult && (
              <ActionButton
                label={backfillRunning ? 'Running...' : 'Run Backfill'}
                variant="primary"
                onClick={runBackfill}
                disabled={backfillRunning}
              />
            )}
          </>
        }
      >
        {!backfillResult && !backfillRunning && (
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            <p>The backfill job will:</p>
            <ul style={{ marginLeft: 20, marginTop: 8 }}>
              <li>Scan every contract with non-zero contract-asset exposure.</li>
              <li>Auto-assign to a pool when credit_tier + duration_bucket + industry + geography match exactly.</li>
              <li>Flag for review when there are multiple matching pools.</li>
              <li>Flag as unassignable when no matching pool exists.</li>
            </ul>
            <p style={{ marginTop: 12 }}>
              Run this before flipping <code>USE_CECL_FOR_CONTRACT_ASSETS = true</code>. The enable flip will fail with
              <strong> CECL_BACKFILL_INCOMPLETE</strong> if unassigned contracts remain.
            </p>
          </div>
        )}

        {backfillRunning && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>
            Running backfill... this may take a moment for large contract populations.
          </div>
        )}

        {backfillResult && (
          <>
            <KpiGrid columns={4} items={[
              { label: 'Scanned', value: backfillResult.total_contracts_scanned },
              { label: 'Auto-assigned', value: backfillResult.auto_assigned_count, color: 'var(--positive)' },
              { label: 'Needs Review', value: backfillResult.needs_review_count, color: 'var(--warning)' },
              { label: 'Unassignable', value: backfillResult.unassignable_count, color: 'var(--accent-critical)' },
            ]} />

            <TabBar
              tabs={[
                { key: 'auto', label: 'Auto-assigned', count: backfillResult.auto_assigned_count },
                { key: 'review', label: 'Needs Review', count: backfillResult.needs_review_count },
                { key: 'unassignable', label: 'Unassignable', count: backfillResult.unassignable_count },
              ]}
              active={backfillTab}
              onChange={setBackfillTab}
            />

            {backfillTab === 'auto' && (
              <DataTable
                columns={idCols}
                rows={tabRows(backfillResult.auto_assigned_contract_ids)}
                rowKey="id"
                emptyMessage="No contracts auto-assigned in this run."
              />
            )}
            {backfillTab === 'review' && (
              <>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                  Multiple pools matched these contracts. Open each contract and manually assign pool or set individual basis.
                </div>
                <DataTable
                  columns={idCols}
                  rows={tabRows(backfillResult.needs_review_contract_ids)}
                  rowKey="id"
                  emptyMessage="No contracts flagged for review."
                />
              </>
            )}
            {backfillTab === 'unassignable' && (
              <>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                  No matching pool exists. Either create a pool covering the contract's risk characteristics, or set an individual basis on the contract.
                </div>
                <DataTable
                  columns={idCols}
                  rows={tabRows(backfillResult.unassignable_contract_ids)}
                  rowKey="id"
                  emptyMessage="No unassignable contracts — every contract matched at least one pool."
                />
              </>
            )}
          </>
        )}
      </Modal>
    </PageLayout>
  );
}
